import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listProducts } from "../lib/api";
import { getProductImage } from "../lib/categoryImages";
import { formatMoney } from "../lib/categories";
import { Flame, TrendingUp } from "lucide-react";

/**
 * TrendingBanner — horizontal auto-scrolling ticker of "hot right now" picks.
 * Pulls top-rated products across mixed categories. Marquee loops seamlessly.
 */
export default function TrendingBanner() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let mounted = true;
    listProducts({ sort: "rating", limit: 24 }).then((data) => {
      if (!mounted) return;
      // Take a mix of categories for variety
      const seen = new Set();
      const mixed = [];
      for (const p of data.items || []) {
        if (seen.has(p.category) && mixed.length < 8) continue;
        seen.add(p.category);
        mixed.push(p);
        if (mixed.length >= 14) break;
      }
      setItems(mixed);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Duplicate list for seamless marquee loop
  const marqueeItems = useMemo(() => (items.length ? [...items, ...items] : []), [items]);

  if (items.length === 0) return null;

  return (
    <section
      className="relative border-b border-neutral-200 bg-neutral-950 text-white overflow-hidden"
      data-testid="trending-banner"
    >
      {/* Left label */}
      <div className="absolute top-0 left-0 h-full z-10 hidden md:flex items-center pl-5 lg:pl-10 pr-4 bg-neutral-950 border-r border-white/10">
        <span className="inline-flex items-center gap-2 text-xs font-medium tracking-wider uppercase text-white">
          <Flame size={14} className="text-brand" />
          Hot right now
        </span>
      </div>
      {/* Mobile label */}
      <div className="md:hidden px-5 py-2 border-b border-white/10">
        <span className="inline-flex items-center gap-2 text-xs font-medium tracking-wider uppercase">
          <Flame size={14} className="text-brand" />
          Hot right now
        </span>
      </div>

      <div className="group py-3 md:pl-40">
        <div className="flex gap-6 animate-[marquee_60s_linear_infinite] group-hover:[animation-play-state:paused] whitespace-nowrap">
          {marqueeItems.map((p, i) => (
            <Link
              key={`${p.id}-${i}`}
              to={`/product/${p.id}`}
              className="inline-flex items-center gap-3 shrink-0 hover:opacity-90"
              data-testid={`trending-item-${i}`}
            >
              <img
                src={getProductImage(p)}
                alt=""
                className="h-9 w-9 rounded-md object-cover bg-neutral-800"
                loading="lazy"
              />
              <span className="inline-flex items-center gap-2">
                <TrendingUp size={12} className="text-brand" />
                <span className="text-sm font-medium">{p.title}</span>
                <span className="text-sm font-bold text-white">{formatMoney(p.price)}</span>
                <span className="text-xs text-white/50">· {p.categoryLabel}</span>
              </span>
              <span className="text-white/20">•</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
